import { Icon } from "armstrong-react";
import React from "react";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { Link } from "react-router";
import * as DateUtil from "@travellocal/utils/dateHelpers";
import { FlowBox, Loader, QuoteExpando } from "../../../core/atoms/atoms";
import { QuoteBox } from "../../../core/molecules/quoteBox";
import { CardContainer } from "../../../shared/components/inspiration/cardContainer";
import { FlexiCard } from "../../../shared/components/inspiration/cards";
import { LocationCard } from "@travellocal/ui";
import { apiPromiseHook, createEnterHookPromise } from "../../../shared/flux/actions/enterHooks";
import { CollectionKeyHelper } from "../../../shared/helpers/contentCollectionHelpers";
import { GoogleAnalyticsHelper } from "@travellocal/ui";
import { buildStructuredDataSnippet } from "../../../shared/helpers/metaData";
import { MatchState } from "../../../shared/helpers/reduxHelpers";
import ResponsiveContainer from "../../../shared/helpers/responsiveContainer";
import * as StringUtil from "../../../shared/helpers/stringHelpers";
import { ShowMoreHeightFade } from "@travellocal/ui";
import { TLSiteStatCard } from "../../components/cards/tlSiteStatCard";
import { SwipeCarousel } from "../../components/swipeCarousel/swipeCarousel";
import * as WebRoutes from "@travellocal/utils/webRoutes"
import { ActionPromises } from "../../state/actionPromises";
import { DetailRatingFragment, LocationUpsellBlock, TLRecommendRating } from "../lto/components";

import "./reviewDetail.scss";

interface StateProps {
  reviewState: IState<IContent>;
  locationState: IState<ILocation>;
}

type Props = StateProps;

const stateToProps = (state: IWebStoreState): StateProps => ({
  reviewState: state.contentItem,
  locationState: state.country,
});

/**
 * View for `/inspiration/review/:inspirationModelId`
 */
class ReviewDetailComponent extends React.Component<Props> {

  private renderHelmet = () => {
    const review = this.props.reviewState;

    return MatchState(review, {
      pending: () => <Helmet title="Loading... | TravelLocal" />,
      ok: () => {
        const fullReview = (review.data.content as IReview);
        if (!fullReview) {
          return null;
        }
        return (
          <Helmet title={review.data.meta.titleTag || review.data.title}
            meta={this.buildMetaTags(review.data)}
            script={[
              this.buildStructuredData(review.data),
            ]}
            link={[
              { rel: "canonical", href: `${globalConfig.wwwBaseUrl}${WebRoutes.contentDetail(review.data, true)}` },
            ]} />
        );
      },
      error: () => <Helmet title="Error | TravelLocal" />,
    });
  }

  private buildMetaTags = (content: IContent) => {
    const metaTagArray = [
      { name: "description", content: content.meta ? content.meta.descriptionTag : null },
      { property: "og:type", content: "article" }, // There is no review opengraph type
      { property: "og:article:published_time",
        content: content.publishedTimestamp ? content.publishedTimestamp.value : null },
      { property: "og:title", content: content.meta && content.meta.titleTag ? content.meta.titleTag : content.title },
      { property: "og:description", content: content.meta ? content.meta.descriptionTag : null },
      { property: "og:url", content: `${globalConfig.wwwBaseUrl}${WebRoutes.contentDetail(content, true)}` },
      { property: "og:image", content: this.getMetaImageUrl(content) },
    ];

    return metaTagArray;
  }

  private getMetaImageUrl = (content: IContent) => {
    if (content.heroImage) {
      return content.heroImage.smallUrl;
    }
    else if (content.locations.length && content.locations[0].heroImage) {
      return content.locations[0].heroImage.smallUrl;
    }
    else {
      return `${globalConfig.wwwBaseUrl}/images/logo-accent.png`;
    }
  }

  private buildStructuredData = (content: IContent) => {
    const commonData = {
      image: this.getMetaImageUrl(content),
      mainEntityofPage: `${globalConfig.wwwBaseUrl}${WebRoutes.contentDetail(content, true)}`,
      datePublished: content.publishedTimestamp ? content.publishedTimestamp.value : null,
      dateCreated: content.createdTimestamp ? content.createdTimestamp.value : null,
      dateModified: content.updatedTimestamp ? content.updatedTimestamp.value : null,
      description: content.meta ? content.meta.descriptionTag : null,
    };
    const fullReview = (content.content as IReview);

    const reviewData = {
      headline: content.title,
      publisher: {
        "@type": "Organization",
        "name": "TravelLocal",
        "logo": {
          type: "ImageObject",
          url: "https://www.travellocal.com/images/logo-green.png",
        },
      },
      author: {
        "@type": "Person",
        "name": content.guestAuthor || `${content.author.firstName} ${content.author.lastName}`,
      },
      reviewRating: {
        "@type": "Rating",
        "ratingValue": fullReview.overallRating,
      },
      itemReviewed: {
        "@type": "TravelAgency",
        "name": "TravelLocal",
        "image": `${globalConfig.wwwBaseUrl}/images/logo-green.svg`,
      },
    };
    const structuredData = { ...commonData, ...reviewData };
    return buildStructuredDataSnippet("Review", structuredData);
  }

  public render() {
    const { reviewState, locationState } = this.props;

    return (
      <div className="review-detail">
        {this.renderHelmet()}
        <FlowBox className="marketing-2cols-sidebar width-wrapper">
          {MatchState(reviewState, {
            none: err => err
              ? (
                <div>
                  <header>
                    <p>{err.message}</p>
                  </header>
                </div>
              )
              : (
                <article className="review">
                  <header>
                    <h1>Loading review... <Loader /></h1>
                  </header>
                </article>
              ),
            data: review => {
              const fullReview = (review.content as IReview);
              if (!fullReview) {
                return null;
              }

              const lto = fullReview.lto;
              const customerName = fullReview.customerName.trim();
              const ltoDisplayName = lto.publicName || lto.name;

              return (
                <article className="review">
                  <header>
                    <h1>{review.title}</h1>
                  </header>

                  <section className="overall-comment">
                    <ShowMoreHeightFade threshold={300}>
                      <p className="customer">{fullReview.overallComment.trim()}</p>
                    </ShowMoreHeightFade>
                  </section>

                  <FlowBox className="review-meta">
                    <p><Icon icon={Icon.Icomoon.user} /> {customerName}, from {fullReview.customerLocation}</p>
                    <p>
                      <Icon icon={Icon.Icomoon.earth2} />
                      {` Visited `}
                      {StringUtil.elementsToSentence(review.locations.map(
                        l => <Link key={l.id} to={WebRoutes.location(l)}>{l.name}</Link>
                      ))}
                      {` in `}
                      <time>{DateUtil.formatDateTime(review.publishedTimestamp, "MMMM YYYY")}</time>
                    </p>
                  </FlowBox>

                  <FlowBox>
                    <div className="rating-summary">
                      <DetailRatingFragment
                        className="total"
                        name={customerName}
                        fragment="their overall experience"
                        rating={fullReview.overallRating}
                      >
                        <p>Overall rating</p>
                      </DetailRatingFragment>

                      <DetailRatingFragment name={customerName} fragment={`${ltoDisplayName}'s planning process`} rating={fullReview.ltoInteractionRating}>
                        <p>Planning</p>
                      </DetailRatingFragment>

                      <DetailRatingFragment name={customerName} fragment={`the guides provided by ${ltoDisplayName}`} rating={fullReview.guideRating}>
                        <p>Guides</p>
                      </DetailRatingFragment>

                      <DetailRatingFragment name={customerName} fragment={`the transportation provided by ${ltoDisplayName}`} rating={fullReview.transportationRating}>
                        <p>Transportation</p>
                      </DetailRatingFragment>

                      <DetailRatingFragment name={customerName} fragment={`the overall value for money`} rating={fullReview.valueRating}>
                        <p>Value</p>
                      </DetailRatingFragment>
                    </div>

                    <TLSiteStatCard />
                  </FlowBox>

                  {fullReview.ltoPositives &&
                    <QuoteExpando question={`What was good about ${ltoDisplayName}?`}>
                      <p>{fullReview.ltoPositives}</p>
                    </QuoteExpando>
                  }

                  {fullReview.ltoImprovements &&
                    <QuoteExpando question={`What could ${ltoDisplayName} improve on?`}>
                      <p>{fullReview.ltoImprovements}</p>
                    </QuoteExpando>
                  }

                  {fullReview.guideComment &&
                    <QuoteExpando question="What did you think about the guides or drivers on your trip?">
                      <p>{fullReview.guideComment}</p>
                    </QuoteExpando>
                  }

                  {fullReview.locationAdvice &&
                    <QuoteExpando
                      question={`What advice would you give to other travellers planning a trip to ${StringUtil.arrayToSentence(review.locations.map(l => l.name), "or")}?`}
                    >
                      <p>{fullReview.locationAdvice}</p>
                    </QuoteExpando>
                  }

                  {fullReview.favoritePart &&
                    <QuoteExpando question="What was the most memorable part of your trip?">
                      <p>{fullReview.favoritePart}</p>
                    </QuoteExpando>
                  }

                  <TLRecommendRating rating={fullReview.howLikelyToRecommend} />

                  {fullReview.reply &&
                    <QuoteBox
                      title={`${StringUtil.capitalize(ltoDisplayName)} replied...`}
                      content={<p>{fullReview.reply}</p>}
                      avatarSource={fullReview.lto.image ? fullReview.lto.image.thumbnailUrl : null}
                      cite={`${StringUtil.capitalize(ltoDisplayName)}`}
                    />
                  }
                </article>
              );
            },
          })}

          <div className="sidebar">
            {MatchState(reviewState, {
              data: ({ locations }) => locations.map(location => (
                <LocationCard
                  location={location}
                  onClick={() => GoogleAnalyticsHelper.logEvent("Select Country", "Click", "Review page card")}
                  key={location.id}
                />
              )),
            })}
          </div>
        </FlowBox>

        {MatchState(locationState, {
          // Enterhook loads primary location of a review
          data: location => (
            <LocationUpsellBlock locations={[location]} />
          ),
        })}

        {MatchState(reviewState, {
          data: review => {
            const relatedContentFilters = {
              locations: review.locations.map(x => x.id),
              types: [ContentType.Review],
            };

            const description = "TravelLocal asks all of its customers for honest feedback on their trips with our partners - this is how we ensure our experts are the best.";

            return (
              <div className="related-content">
                <div className="width-wrapper">
                  <header>
                    <h3>More reviews...</h3>
                  </header>

                  <ResponsiveContainer
                    mobile={() => (
                      <CardContainer
                        collectionKey={CollectionKeyHelper.ContentDetailRelated(review.id)}
                        filters={relatedContentFilters}
                        seedItem={review}
                        cardLimit={3}
                        renderCardWrapper={elements => (
                          <SwipeCarousel>
                            {elements}
                            <FlexiCard
                              to={WebRoutes.inspiration({ types: [ContentType.Review] })}
                              className="cta-card is-marginless"
                              icomoon={Icon.Icomoon.quotesLeft}
                              title="Quality"
                              description={description}
                              cta={`See more reviews`}
                            />
                          </SwipeCarousel>
                        )}
                      />
                    )}
                    tablet={() => (
                      <CardContainer
                        collectionKey={CollectionKeyHelper.ContentDetailRelated(review.id)}
                        filters={relatedContentFilters}
                        seedItem={review}
                        cardLimit={3}
                        columnClassName="column is-6-tablet is-4-desktop is-3-widescreen is-3-fullhd"
                      >
                        <FlexiCard
                          to={WebRoutes.inspiration({ types: [ContentType.Review] })}
                          className="cta-card"
                          icomoon={Icon.Icomoon.quotesLeft}
                          title="Quality"
                          description={description}
                          cta={`See more reviews`}
                        />
                      </CardContainer>
                    )}
                  />
                </div>
              </div>
            );
          },
        })}
      </div>
    );
  }
}

export const ReviewDetail = connect(stateToProps)(ReviewDetailComponent);

export const reviewDetailOnEnter = apiPromiseHook(
  createEnterHookPromise((n) => ActionPromises.initialiseReviewDetail(parseInt(n.params.id))),
);
